"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useHomepageAnalytics } from "@/context/AnalyticsProvider/homepage";
import { cn } from "@/lib/utils";
import { formatGenreLabel } from "@/utils/string";
import debounce from "lodash/debounce";
import { Crown, Gem, Search, Star, Trophy, X } from "lucide-react";
import { useCallback } from "react";
import { type FilterOptions, useQuestFilter } from "./QuestFilterProvider";

const getEarningsLabel = (reward: string): string => {
  const labelMap: Record<string, string> = {
    point: "Gems",
    weekly_point: "Weekly Points",
    campaign_point: "Leaderboard Points",
  };
  return labelMap[reward.toLowerCase()] || reward;
};

const getEarningsIcon = (reward: string) => {
  const normalized = reward.toLowerCase();
  if (normalized === "point") return <Gem className="w-4 h-4" />;
  if (normalized === "weekly_point") {
    return <Crown className="w-4 h-4" />;
  }
  if (normalized === "campaign_point") return <Star className="w-4 h-4" />;
  return <Trophy className="w-4 h-4" />;
};

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      {children}
    </div>
  );
}

interface FilterChipProps {
  label: string;
  selected: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
  removable?: boolean;
}

function FilterChip({
  label,
  selected,
  onToggle,
  icon,
  removable = false,
}: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
        selected
          ? "bg-white text-neutral-900"
          : "bg-white/10 text-white hover:bg-white/20 border border-white/20",
      )}
    >
      <span>{label}</span>
      {icon && <span className="[&>*]:w-3 [&>*]:h-3">{icon}</span>}
      {selected && removable && (
        <X className="w-3 h-3 ml-0.5" strokeWidth={2.5} />
      )}
    </button>
  );
}

function ToggleSwitch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-white/70 group-hover:text-white transition-colors">
        {label}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          checked ? "bg-white" : "bg-white/20",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full transition-transform",
            checked ? "translate-x-6 bg-neutral-900" : "translate-x-1 bg-white",
          )}
        />
      </button>
    </div>
  );
}

interface QuestFilterPanelProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

// Mapping of filter keys to control names for analytics
const filterKeyToControl: Partial<Record<keyof FilterOptions, string>> = {
  gameTitle: "GameTitleSearch",
  platforms: "PlatformFilter",
  rewards: "RewardFilter",
  questTypes: "QuestTypeFilter",
  genres: "GenreFilter",
  hideCompleted: "HideCompletedToggle",
};

export function QuestFilterPanel({
  onClose,
  showCloseButton = false,
}: QuestFilterPanelProps) {
  const {
    filters,
    setFilters,
    totalQuestCount,
    availablePlatforms,
    availableGenres,
    availableRewards,
    availableQuestTypes,
    resetFilters,
  } = useQuestFilter();
  const { track } = useHomepageAnalytics();

  const handleResetFilters = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ClearAllFilters",
        controlType: ControlType.BUTTON,
      },
    });
    resetFilters();
  };

  // Generic toggle function for any filter type
  const toggleFilter = (filterKey: keyof FilterOptions, value: string) => {
    setFilters((prev) => {
      const currentSet = prev[filterKey] as Set<string>;
      const newSet = new Set(currentSet);
      const isRemoving = newSet.has(value);

      if (isRemoving) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }

      track({
        action: Action.PRESSED,
        extras: {
          id: "quest-finder",
          control: filterKeyToControl[filterKey] || "",
          controlType: ControlType.BUTTON,
          filterValue: value,
          filterAction: isRemoving ? "removed" : "added",
        },
      });

      return { ...prev, [filterKey]: newSet };
    });
  };

  // debounced tracking for game title search
  const debouncedTrackGameTitleSearch = useCallback(
    debounce((value: string) => {
      track({
        action: Action.PRESSED,
        extras: {
          id: "quest-finder",
          control: filterKeyToControl.gameTitle || "",
          controlType: ControlType.BUTTON,
          filterValue: value,
          filterAction: "searching",
        },
      });
    }, 1000),
    [],
  );

  const handleGameTitleChange = (value: string) => {
    setFilters((prev) => ({ ...prev, gameTitle: value }));
    if (value) debouncedTrackGameTitleSearch(value);
  };

  // Handle clearing game title
  const handleClearGameTitle = () => {
    setFilters((prev) => ({ ...prev, gameTitle: "" }));

    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: filterKeyToControl.gameTitle || "",
        controlType: ControlType.BUTTON,
        filterValue: "",
        filterAction: "cleared",
      },
    });
  };

  // Handle hideCompleted toggle with tracking
  const handleHideCompletedToggle = (checked: boolean) => {
    setFilters((prev) => ({ ...prev, hideCompleted: checked }));

    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: filterKeyToControl.hideCompleted || "",
        controlType: ControlType.BUTTON,
        filterValue: checked ? "true" : "false",
        filterAction: checked ? "enabled" : "disabled",
      },
    });
  };

  const getActiveFilters = () => {
    const active: Array<{
      key: string;
      label: string;
      icon?: React.ReactNode;
      onRemove: () => void;
    }> = [];

    // Platform filters
    for (const platform of filters.platforms) {
      active.push({
        key: `platform-${platform}`,
        label: platform,
        onRemove: () => toggleFilter("platforms", platform),
      });
    }

    // Reward filters
    for (const reward of filters.rewards) {
      active.push({
        key: `reward-${reward}`,
        label: getEarningsLabel(reward),
        onRemove: () => toggleFilter("rewards", reward),
      });
    }

    // Quest Type filters
    for (const questType of filters.questTypes) {
      active.push({
        key: `questType-${questType}`,
        label: questType,
        onRemove: () => toggleFilter("questTypes", questType),
      });
    }

    // Genre filters
    for (const genre of filters.genres) {
      active.push({
        key: `genre-${genre}`,
        label: formatGenreLabel(genre),
        onRemove: () => toggleFilter("genres", genre),
      });
    }

    // Game Title filter
    if (filters.gameTitle) {
      active.push({
        key: "gameTitle",
        label: `Game: ${filters.gameTitle}`,
        icon: <Search className="w-4 h-4" />,
        onRemove: handleClearGameTitle,
      });
    }

    return active;
  };

  return (
    <div className="bg-neutral-900/95 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-6 h-fit">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Filter Quests</h2>
        <div className="flex items-center gap-3">
          <p className="text-sm text-white/50">{totalQuestCount} quests</p>
          {showCloseButton && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Close filters"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>
      {/* Active Filters Section */}
      {(() => {
        const activeFilters = getActiveFilters();
        return activeFilters.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-white/50">
                Active Filters ({activeFilters.length})
              </p>
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-xs text-white/70 hover:text-white underline transition-colors"
              >
                Clear All
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((filter) => (
                <FilterChip
                  key={filter.key}
                  label={filter.label}
                  selected={true}
                  onToggle={filter.onRemove}
                  icon={filter.icon}
                  removable={true}
                />
              ))}
            </div>
          </div>
        ) : null;
      })()}
      <div className="w-full h-px bg-white/50 mb-6" />
      {/* Platform */}
      {availablePlatforms.length > 0 && (
        <FilterSection title="Platform">
          <div className="flex flex-wrap gap-2">
            {availablePlatforms.map((platform) => (
              <FilterChip
                key={platform}
                label={platform}
                selected={filters.platforms.has(platform)}
                onToggle={() => toggleFilter("platforms", platform)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Rewards */}
      {availableRewards.length > 0 && (
        <FilterSection title="Rewards">
          <div className="flex flex-wrap gap-2">
            {availableRewards.map((reward) => (
              <FilterChip
                key={reward}
                label={getEarningsLabel(reward)}
                selected={filters.rewards.has(reward)}
                onToggle={() => toggleFilter("rewards", reward)}
                icon={getEarningsIcon(reward)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Quest Type */}
      {availableQuestTypes.length > 0 && (
        <FilterSection title="Quest Type">
          <div className="flex flex-wrap gap-2">
            {availableQuestTypes.map((questType) => (
              <FilterChip
                key={questType}
                label={questType}
                selected={filters.questTypes.has(questType)}
                onToggle={() => toggleFilter("questTypes", questType)}
              />
            ))}
          </div>
        </FilterSection>
      )}
      {/* Genre */}
      {availableGenres.length > 0 && (
        <FilterSection title="Genre">
          <div className="flex flex-wrap gap-2">
            {availableGenres.map((genre) => (
              <FilterChip
                key={genre}
                label={formatGenreLabel(genre)}
                selected={filters.genres.has(genre)}
                onToggle={() => toggleFilter("genres", genre)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* Game Title Search */}
      <FilterSection title="Game Name">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Chainers"
            value={filters.gameTitle}
            onChange={(e) => handleGameTitleChange(e.target.value)}
            className="pl-9 bg-white/5 border-white/20 text-white placeholder:text-white/40"
          />
          {filters.gameTitle && (
            <button
              type="button"
              onClick={handleClearGameTitle}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </FilterSection>

      <ToggleSwitch
        label="Hide Quests I've Completed"
        checked={filters.hideCompleted}
        onChange={handleHideCompletedToggle}
      />

      {showCloseButton && onClose && (
        <div className="flex gap-3 lg:hidden">
          <Button
            onClick={resetFilters}
            variant="outline"
            size="lg"
            className="flex-1 border-neutral-700 rounded-xl"
          >
            Clear All
          </Button>
          <Button
            onClick={onClose}
            variant="default"
            size="lg"
            className="flex-1 rounded-xl"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
